import { Box, TextField, Typography } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { Button } from "components";
import { useEffect, useState } from "react";
import Drawer from "shared/Drawer";

import { BOOL_OPTIONS, CURVA_OPTIONS, LATEST_RUN_OPTIONS } from "../../mocks";
import { useStyles } from "./styles";

const FilterDrawer = ({
  open,
  onClose,
  onApply,
  onClear,
  initialParams,
  categories
}) => {
  const classes = useStyles();

  const [latestRun, setLatestRun] = useState(initialParams?.latest_run ?? true);
  const [runId, setRunId] = useState(initialParams?.run_id ?? "");
  const [dateFrom, setDateFrom] = useState(
    initialParams?.reference_date_from ?? ""
  );
  const [dateTo, setDateTo] = useState(initialParams?.reference_date_to ?? "");
  const [selectedCategory, setSelectedCategory] = useState(
    initialParams?.categoria ?? null
  );
  const [curva, setCurva] = useState(initialParams?.curva ?? null);
  const [unavailabilityEligible, setUnavailabilityEligible] = useState(
    initialParams?.unavailability_eligible ?? null
  );
  const [promoEligibleFlag, setPromoEligibleFlag] = useState(
    initialParams?.promo_eligible_flag ?? null
  );
  const [atingeZeroWaste, setAtingeZeroWaste] = useState(
    initialParams?.atinge_zero_waste ?? null
  );

  useEffect(() => {
    if (!initialParams) return;
    setLatestRun(initialParams.latest_run ?? true);
    setRunId(initialParams.run_id ?? "");
    setDateFrom(initialParams.reference_date_from ?? "");
    setDateTo(initialParams.reference_date_to ?? "");
    setSelectedCategory(initialParams.categoria ?? null);
    setCurva(initialParams.curva ?? null);
    setUnavailabilityEligible(initialParams.unavailability_eligible ?? null);
    setPromoEligibleFlag(initialParams.promo_eligible_flag ?? null);
    setAtingeZeroWaste(initialParams.atinge_zero_waste ?? null);
  }, [initialParams]);

  const handleApply = () => {
    const parsedRunId = runId !== "" ? parseInt(runId, 10) : undefined;
    onApply({
      latest_run: parsedRunId ? undefined : latestRun,
      run_id: parsedRunId,
      reference_date_from: dateFrom || undefined,
      reference_date_to: dateTo || undefined,
      categoria: selectedCategory || undefined,
      curva: curva !== null ? curva : undefined,
      unavailability_eligible:
        unavailabilityEligible !== null ? unavailabilityEligible : undefined,
      promo_eligible_flag:
        promoEligibleFlag !== null ? promoEligibleFlag : undefined,
      atinge_zero_waste: atingeZeroWaste !== null ? atingeZeroWaste : undefined
    });
    onClose();
  };

  const handleClear = () => {
    setLatestRun(true);
    setRunId("");
    setDateFrom("");
    setDateTo("");
    setSelectedCategory(null);
    setCurva(null);
    setUnavailabilityEligible(null);
    setPromoEligibleFlag(null);
    setAtingeZeroWaste(null);
    onClear();
    onClose();
  };

  return (
    <Drawer open={open} onClose={onClose} title="Filtros" anchor="right">
      <Box className={classes.drawerContent}>
        <Box className={classes.scrollableContent}>
          {/* Execução */}
          <Box className={`${classes.filterSection} ${classes.inputSection}`}>
            <Typography className={classes.sectionTitle}>Execução</Typography>
            <Typography className={classes.sectionSubtitle}>
              Tipo de execução
            </Typography>
            <Autocomplete
              options={LATEST_RUN_OPTIONS}
              getOptionLabel={option => option.label}
              value={
                LATEST_RUN_OPTIONS.find(o => o.value === latestRun) || null
              }
              onChange={(_, newValue) => {
                setLatestRun(newValue?.value ?? true);
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Selecione"
                />
              )}
            />
            <Box style={{ marginTop: "0.75rem" }}>
              <Typography className={classes.sectionSubtitle}>
                ID de execução específica
              </Typography>
              <TextField
                variant="outlined"
                placeholder="Ex: 539"
                value={runId}
                onChange={e => {
                  const val = e.target.value;
                  if (val === "" || /^\d+$/.test(val)) setRunId(val);
                }}
                inputProps={{ inputMode: "numeric" }}
                fullWidth
              />
            </Box>
          </Box>

          {/* Data de referência */}
          <Box className={`${classes.filterSection} ${classes.inputSection}`}>
            <Typography className={classes.sectionTitle}>
              Data de referência
            </Typography>
            <Box className={classes.dateRow}>
              <Box flex={1}>
                <Typography className={classes.sectionSubtitle}>De</Typography>
                <TextField
                  type="date"
                  variant="outlined"
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Box>
              <Box flex={1}>
                <Typography className={classes.sectionSubtitle}>Até</Typography>
                <TextField
                  type="date"
                  variant="outlined"
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Box>
            </Box>
          </Box>

          {/* Produto */}
          <Box className={`${classes.filterSection} ${classes.inputSection}`}>
            <Typography className={classes.sectionTitle}>Produto</Typography>
            <Autocomplete
              options={categories}
              getOptionLabel={option => option.label}
              value={categories.find(c => c.id === selectedCategory) || null}
              onChange={(_, newValue) => {
                setSelectedCategory(newValue?.id ?? null);
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Selecione a categoria"
                />
              )}
            />
            <Box style={{ marginTop: "0.75rem" }}>
              <Typography className={classes.sectionSubtitle}>Curva</Typography>
              <Autocomplete
                options={CURVA_OPTIONS}
                getOptionLabel={option => option.label}
                value={CURVA_OPTIONS.find(o => o.value === curva) || null}
                onChange={(_, newValue) => {
                  setCurva(newValue?.value ?? null);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Todas"
                  />
                )}
              />
            </Box>
          </Box>

          {/* Flags */}
          <Box className={`${classes.filterSection} ${classes.inputSection}`}>
            <Typography className={classes.sectionTitle}>Flags</Typography>

            <Typography className={classes.sectionSubtitle}>
              Elegível para indisponibilidade (unavailability_eligible)
            </Typography>
            <Autocomplete
              options={BOOL_OPTIONS}
              getOptionLabel={option => option.label}
              value={
                BOOL_OPTIONS.find(o => o.value === unavailabilityEligible) ||
                null
              }
              onChange={(_, newValue) => {
                setUnavailabilityEligible(newValue?.value ?? null);
              }}
              renderInput={params => (
                <TextField {...params} variant="outlined" placeholder="Todos" />
              )}
            />

            <Box style={{ marginTop: "0.75rem" }}>
              <Typography className={classes.sectionSubtitle}>
                Elegível para promoção (promo_eligible_flag)
              </Typography>
              <Autocomplete
                options={BOOL_OPTIONS}
                getOptionLabel={option => option.label}
                value={
                  BOOL_OPTIONS.find(o => o.value === promoEligibleFlag) || null
                }
                onChange={(_, newValue) => {
                  setPromoEligibleFlag(newValue?.value ?? null);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Todos"
                  />
                )}
              />
            </Box>

            <Box style={{ marginTop: "0.75rem" }}>
              <Typography className={classes.sectionSubtitle}>
                Atinge zero waste
              </Typography>
              <Autocomplete
                options={BOOL_OPTIONS}
                getOptionLabel={option => option.label}
                value={
                  BOOL_OPTIONS.find(o => o.value === atingeZeroWaste) || null
                }
                onChange={(_, newValue) => {
                  setAtingeZeroWaste(newValue?.value ?? null);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Todos"
                  />
                )}
              />
            </Box>
          </Box>
        </Box>

        <Box className={classes.buttonsContainer}>
          <Button
            variant="outlined"
            color="default"
            onClick={handleClear}
            fullWidth
            className={classes.clearButton}
          >
            Limpar filtros
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApply}
            fullWidth
          >
            Aplicar filtros
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default FilterDrawer;
